/** *
 * Fixing all no-param-reasign issue is too dangerous now.
 * Disabling lint
 *
 * TODO: fix no-param-reassing issues
 */

/* eslint-disable no-param-reassign */
import axios from 'axios'
import {
  dateFormatterUTC,
  shippingMethodMapping as nameMapping,
} from '@usap/core'
import apiConfig from '../../config/api-config'
import { getSingleState } from './localForage'
import getBufferETAFlag from './getBufferETAFlag'

const batchCount = 6

const apiUrl = `${
  apiConfig.apiURL
}/v1/shipping-options/getEstimatedDeliveryDates`
const apiUrlBatch = `${
  apiConfig.apiURL
}/v1/shipping-options/getEstimatedDeliveryDatesBatch`

const getLabel = method => nameMapping.find(obj => obj.method === method).label

const getMethodName = method => nameMapping.find(obj => obj.method === method).name

const getCacheKey = () => {
  const tDate = new Date()
  return `${tDate.getUTCFullYear()}-${tDate.getUTCMonth()
    + 1}-${tDate.getUTCDate()}T13:00:00-0500`
}

const getCurrentZip = async zip => {
  const [, location] = await getSingleState('location')
  return zip || (location && location.postalCode) || '61301'
}

const getItems = (data = [], qty, zip, batch) => {
  data = Array.isArray(data) ? data : [data]
  return data.reduce((prev, {
    sku, brand, skuNumber, quantity,
  }) => [
    ...prev,
    {
      sku: sku || skuNumber,
      brand,
      qty: qty || quantity || 1,
      ...batch
        ? {}
        : {
          zip,
          exp: getCacheKey(),
        },
    },
  ], [])
}

const getDeliveryDates = (options, trim) => Object.entries(options).reduce(
  (prev, [method, date]) => [
    ...prev,
    {
      label: getLabel(method),
      name: getMethodName(method),
      formattedDate: dateFormatterUTC(date, trim),
      method,
      date,
    },
  ], [],
)

const getAvailability = errors => !errors.find(
  item => Object.values(item)[0]?.toLowerCase().includes('out of stock'))

const getInventoryDetails = (_stock = 0) => {
  const stock = Number(_stock)
  const available = !stock <= 0
  return {
    available,
    count: stock > 50 ? 50 : stock,
    message:
      available && stock < 6
        ? `${stock} ${stock === 1 ? 'Unit' : 'Units'} Left`
        : '',
    dropCount: stock > 50 ? 50 : stock,
  }
}

const getToken = async () => {
  const isCheckout = __BROWSER__ && window.location.pathname.includes('/checkout')
  const [, token] = await getSingleState('token')
  let accessToken = ''
  try {
    accessToken = token.access_token
  } catch {
    accessToken = ''
  }
  return accessToken && isCheckout
    ? {
      'access-token': accessToken,
    }
    : {}
}

const getOrderId = async () => {
  const isCheckout = __BROWSER__ && window.location.pathname.includes('/checkout')
  const [, order] = await getSingleState('cart')
  const [, buynow] = await getSingleState('buyNowCart')
  let orderId = ''

  try {
    orderId = order.orderId
    if (buynow.buyNowCartDB && buynow.buyNowCartDB.orderId) {
      orderId = buynow.buyNowCartDB.orderId
    }
  } catch {
    orderId = ''
  }
  return orderId && isCheckout
    ? {
      orderId: `CPZ${orderId}`,
    }
    : {}
}

const callShippingExp = async (
  params,
  currIndex,
  updateData,
  expData = {},
  trim,
) => {
  if (__BROWSER__ && !window.__HAS_COMPOSITE_LOADED__) return ''
  if (currIndex < params.length) {
    const currZip = await getCurrentZip()
    if ((params[currIndex] || {}).zip !== currZip) return ''
    const token = await getToken()
    const orderId = await getOrderId()
    const bufferETAFlag = getBufferETAFlag()
    return axios
      .get(apiUrl, {
        params: { ...params[currIndex], ...orderId, buffereta: bufferETAFlag },
        headers: { apikey: apiConfig.apiKey, ...token },
      })
      .then(({ data: { data: _data } = {} }) => {
        const data = _data || {}
        expData = {
          ...expData,
          [data.sku]: {
            sku: data.sku,
            brand: data.brandText,
            deliveryDates: getDeliveryDates(data.shippingOptions || {}),
            inStock: getAvailability(data.errors || {}),
            inventoryDetails: getInventoryDetails(data.stock),
          },
        }
        updateData(expData)
        callShippingExp(params, currIndex + 1, updateData, expData, trim)
      })
      .catch(() => {
        console.error(`Unable to get data for ${params[currIndex].sku}`)
        callShippingExp(params, currIndex + 1, updateData, expData, trim)
      })
  }
  return ''
}

const callShippingExpBatch = async (
  zipCode,
  methods,
  qty,
  trim,
  updateData,
  batches,
  currIndex,
  batchRes = {},
) => {
  if (__BROWSER__ && !window.__HAS_COMPOSITE_LOADED__) return ''
  if (currIndex < batches.length) {
    const currZip = await getCurrentZip(zipCode)
    if (zipCode !== currZip) return ''
    const payload = batches[currIndex]
    const token = await getToken()
    const orderId = await getOrderId()
    const bufferETAFlag = getBufferETAFlag()
    const body = {
      zip: zipCode,
      buffereta: bufferETAFlag,
      methods: methods || ['Ground', '2 Day', 'Overnight'],
      exp: getCacheKey(),
      items: getItems(payload, qty, zipCode, true),
      ...orderId,
    }
    return axios
      .post(apiUrlBatch, body, {
        headers: { apikey: apiConfig.apiKey, ...token },
      })
      .then(({ data: { data } = {} }) => {
        data = Array.isArray(data) ? data : []
        const response = data.reduce((prev, item) => ({
          ...prev,
          [item.sku]: {
            sku: item.sku,
            brand: item.brandText,
            deliveryDates: getDeliveryDates(item.shippingOptions || {}, trim),
            inStock: getAvailability(item.errors || {}),
            inventoryDetails: getInventoryDetails(item.stock),
          },
        }), {})
        batchRes = { ...batchRes, ...response }
        updateData(batchRes)
        callShippingExpBatch(
          zipCode,
          methods,
          qty,
          trim,
          updateData,
          batches,
          currIndex + 1,
          batchRes,
        )
      })
      .catch(() => {
        console.error(
          `Unable to get data for batch call for batch ${batches[currIndex]}`,
        )
        callShippingExpBatch(
          zipCode,
          methods,
          qty,
          trim,
          updateData,
          batches,
          currIndex + 1,
          batchRes,
        )
      })
  }
  return ''
}

const getShippingExpBatch = (
  _payload,
  zipCode,
  methods,
  qty,
  trim,
  updateData,
) => {
  const payload = Array.isArray(_payload) ? _payload : [_payload]
  let tempBatch = []
  const batchPayload = payload.reduce((prev, item, index) => {
    if (!((index + 1) % batchCount) || index === payload.length - 1) {
      tempBatch.push(item)
      prev.push(tempBatch)
      tempBatch = []
      return prev
    }
    tempBatch.push(item)
    return prev
  }, [])
  callShippingExpBatch(
    zipCode,
    methods,
    qty,
    trim,
    updateData,
    batchPayload,
    0,
  )
}

const getSEDate = async (
  payload,
  qty,
  zip,
  updateData = () => {},
  batch = false,
  methods,
  trim = false,
) => {
  const zipCode = await getCurrentZip(zip)
  if (!zipCode) return Promise.resolve({})
  const paramsArray = getItems(payload, qty, zipCode, batch)
  if (!batch) return callShippingExp(paramsArray, 0, updateData, {}, trim)
  return getShippingExpBatch(payload, zipCode, methods, qty, trim, updateData)
}

export const getSyncSEDetails = async (payload, batch = true, method, forceZipCode) => {
  const zipCode = forceZipCode || await getCurrentZip()
  payload = Array.isArray(payload) ? payload : [payload]
  const items = getItems(payload, undefined, zipCode, batch)
  const rest = batch ? { items } : { ...items[0] }
  const token = await getToken()
  const orderId = await getOrderId()
  const bufferETAFlag = getBufferETAFlag()
  const body = {
    zip: zipCode,
    buffereta: bufferETAFlag,
    methods: method || ['Ground', '2 Day', 'Overnight'],
    exp: getCacheKey(),
    ...rest,
    ...orderId,
  }
  return (batch
    ? axios.post(apiUrlBatch, body, {
      headers: { apikey: apiConfig.apiKey, ...token },
    })
    : axios.get(apiUrl, {
      params: body,
      headers: { apikey: apiConfig.apiKey, ...token },
    })
  )
    .then(({ data: { data } = {} }) => {
      data = Array.isArray(data) ? data : [data]
      return data.reduce((prev, item) => ({
        ...prev,
        [item.sku]: {
          sku: item.sku,
          brand: item.brandText,
          deliveryDates: getDeliveryDates(item.shippingOptions || {}, !batch),
          inStock: getAvailability(item.errors || {}),
          inventoryDetails: getInventoryDetails(item.stock),
          cutoffTime: item?.cutoffTime,
          deliveryType: item?.deliveryType,
          shippingOptions: item?.shippingOptions,
        },
      }), {})
    })
    .catch(err => {
      // PEP-2028
      // nrLogger.error('getShipingExpFail', err)
      throw err
    })
}

export default getSEDate

/* eslint-enable no-param-reassign */
